import './WizardStepper.css';

export interface StepDef {
  key: string;
  label: string;
}

interface Props {
  steps: StepDef[];
  /** Index of the active step. */
  current: number;
  /** Highest index the user has unlocked (drives the ✓ "done" styling). */
  maxReached: number;
  /** Free navigation: jump to ANY step (tabs), not just reached ones. */
  onJump?: (index: number) => void;
  /** Keys of steps that have been reached but are not yet valid — flagged with
   *  a "!" marker so jumping around makes incomplete steps obvious. */
  invalidKeys?: Set<string>;
}

/**
 * Horizontal step indicator for the post wizard. Behaves like tabs — every
 * step is clickable. Completed valid steps show ✓; reached-but-incomplete
 * steps show "!"; the rest show their number.
 */
export function WizardStepper({ steps, current, maxReached, onJump, invalidKeys }: Props) {
  return (
    <ol className="wizstep" aria-label="Progress">
      {steps.map((s, i) => {
        const reached = i <= maxReached;
        const invalid = reached && i !== current && !!invalidKeys?.has(s.key);
        const state =
          i === current ? 'on'
            : invalid ? 'invalid'
              : i < current ? 'done'
                : reached ? 'reached'
                  : 'todo';
        const clickable = !!onJump && i !== current;
        const marker = invalid ? '!' : state === 'done' ? '✓' : i + 1;
        return (
          <li
            key={s.key}
            className={`wizstep__item wizstep__item--${state}`}
            aria-current={i === current ? 'step' : undefined}
          >
            <button
              type="button"
              className="wizstep__btn"
              onClick={clickable ? () => onJump!(i) : undefined}
              disabled={!clickable}
              title={invalid ? `${s.label}: incomplete` : s.label}
            >
              <span className="wizstep__dot">{marker}</span>
              <span className="wizstep__label">{s.label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
